import { SignInPage } from '@backstage/core-components';
import * as React from 'react';
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Link, Typography } from '@material-ui/core';

// Badal.io brand colors
const badalPrimaryBlue = '#1e87f0';
const badalTeal = '#5bbad5';

const useStyles = makeStyles(theme => ({
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${badalPrimaryBlue} 0%, ${badalTeal} 100%)`,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 50%, rgba(30, 135, 240, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(91, 186, 213, 0.3) 0%, transparent 50%)',
    },
  },
  signInPage: {
    zIndex: 1,
    '& .MuiPaper-root': {
      padding: theme.spacing(4),
      minWidth: 500,
      maxWidth: 650,
      boxShadow: '0 30px 60px rgba(0,0,0,0.12), 0 10px 20px rgba(0,0,0,0.08)',
      borderRadius: theme.spacing(2),
    },
    // Hide the BackstageHeader banner
    '& [class*="BackstageHeader"]': {
      display: 'none !important',
    },
    '& header': {
      display: 'none !important',
    },
    // Hide all possible header/title elements
    '& .MuiCardHeader-root': {
      display: 'none !important',
    },
    '& .MuiTypography-h5': {
      display: 'none !important',
    },
    '& .MuiTypography-h4': {
      display: 'none !important',
    },
    '& .MuiCardMedia-root': {
      display: 'none !important',
    },
    '& img[alt="Backstage"]': {
      display: 'none !important',
    },
    // Hide any div that might contain the banner/title at the top
    '& .MuiBox-root:first-child': {
      '& .MuiTypography-root': {
        display: 'none !important',
      },
    },
    '& .MuiCardContent-root': {
      padding: theme.spacing(3),
      paddingTop: theme.spacing(2),
      // Hide any title elements within content
      '& > .MuiBox-root:first-child .MuiTypography-root': {
        display: 'none !important',
      },
    },
    '& .MuiButton-root': {
      margin: theme.spacing(1, 0),
      padding: theme.spacing(1.5),
      fontSize: '1rem',
      fontWeight: 600,
      borderRadius: '8px',
      textTransform: 'none',
    },
  },
  logoSection: {
    textAlign: 'center',
    padding: `${theme.spacing(4)}px 0`,
    zIndex: 1,
  },
  logo: {
    height: 'auto',
    maxHeight: 120,
    width: 'auto',
    maxWidth: '80%',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  footer: {
    position: 'absolute',
    bottom: theme.spacing(3),
    left: 0,
    right: 0,
    textAlign: 'center',
    zIndex: 1,
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(3),
  },
  footerLink: {
    color: 'rgba(255, 255, 255, 0.9)',
    textDecoration: 'none',
    fontSize: '0.875rem',
    '&:hover': {
      color: '#ffffff',
      textDecoration: 'underline',
    },
  },
  copyright: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
}));

export const CustomSignInPage = (props: React.ComponentProps<typeof SignInPage>) => {
  const classes = useStyles();

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.logoSection}>
        <img 
          src="/badal-logo.png" 
          alt="Badal" 
          className={classes.logo}
        />
      </Box>
      <Box className={classes.signInPage}>
        <SignInPage
          {...props}
          auto
          align="center"
          title=""
          providers={[
            {
              id: 'github-auth-provider',
              title: 'GitHub',
              message: 'Sign in using GitHub',
              apiRef: githubAuthApiRef,
            },
          ]}
        />
      </Box>
      <Box className={classes.footer}>
        <Box className={classes.footerLinks}>
          <Link href="/privacy-policy" className={classes.footerLink}>
            Privacy Policy
          </Link>
          <Link href="/terms-of-service" className={classes.footerLink}>
            Terms of Service
          </Link>
        </Box>
        <Typography className={classes.copyright}>
          © {new Date().getFullYear()} Badal by TELUS. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};
